import { updateCursorStyles } from "./cursor/updateCursorStyles";
import {
  removeAxisFromInteraction,
  subscribeToInteractionState
} from "./mutable-state/interactions";
import { assert } from "../utils/assert";
import type { CaughtError } from "../utils/CaughtError";
import { calculateAvailableAxisSize } from "./dom/calculateAvailableAxisSize";
import { calculateHitRegions } from "./dom/calculateHitRegions";
import { calculateItemConstraints } from "./dom/calculateItemConstraints";
import { onDocumentContextMenu } from "./event-handlers/onDocumentContextMenu";
import { onDocumentDoubleClick } from "./event-handlers/onDocumentDoubleClick";
import { onDocumentKeyDown } from "./event-handlers/onDocumentKeyDown";
import { onDocumentLostPointerCapture } from "./event-handlers/onDocumentLostPointerCapture";
import { onDocumentPointerCancel } from "./event-handlers/onDocumentPointerCancel";
import { onDocumentPointerDown } from "./event-handlers/onDocumentPointerDown";
import { onDocumentPointerLeave } from "./event-handlers/onDocumentPointerLeave";
import { onDocumentPointerMove } from "./event-handlers/onDocumentPointerMove";
import { onDocumentPointerOut } from "./event-handlers/onDocumentPointerOut";
import { onDocumentPointerUp } from "./event-handlers/onDocumentPointerUp";
import { onWindowBlur } from "./event-handlers/onWindowBlur";
import { onWindowPageHide } from "./event-handlers/onWindowPageHide";
import {
  deleteMutableAxis,
  endAxisChangeBatch,
  getMountedAxisState,
  startAxisChangeBatch,
  updateMountedAxis
} from "./mutable-state/axes";
import type { SeparatorToItemsMap } from "./mutable-state/types";
import type { RegisteredResizeAxis } from "./types";
import { getDefaultLayout } from "./utils/getDefaultLayout";
import { normalizeLayout } from "./utils/normalizeLayout";
import { notifyItemOnResize } from "./utils/notifyItemOnResize";
import { syncAxisStateToCurrentSize } from "./utils/syncAxisStateToCurrentSize";
import { validateAxisLayout } from "./utils/validateAxisLayout";

const ownerDocumentReferenceCounts = new Map<Document, number>();

let didSubscribeToInteractionState = false;

export function mountAxis(axis: RegisteredResizeAxis) {
  let isMounted = true;

  assert(
    axis.element.ownerDocument.defaultView,
    "Cannot register an unmounted Group"
  );

  const ResizeObserver = axis.element.ownerDocument.defaultView.ResizeObserver;

  const itemIds = new Set<string>();
  const separatorIds = new Set<string>();

  // Add Panels with onResize callbacks to ResizeObserver
  // Add Group to ResizeObserver also in order to sync % based constraints
  const resizeObserver = new ResizeObserver((entries) => {
    // Panel onResize and layout change callbacks may throw;
    // defer errors so that every entry is still processed
    let caughtError: CaughtError | undefined;

    startAxisChangeBatch();
    try {
      for (const entry of entries) {
        const { borderBoxSize, target } = entry;
        if (target === axis.element) {
          if (isMounted) {
            const axisState = getMountedAxisState(axis.id);
            if (!axisState) {
              // Not mounted yet
              break;
            }

            const synced = syncAxisStateToCurrentSize({ axis, axisState });
            if (!synced) {
              // Can't calculate anything meaningful if the group has a width/height of 0
              break;
            }

            if (!synced.changed) {
              continue;
            }

            updateMountedAxis(axis, synced.state);
          }
        } else {
          try {
            notifyItemOnResize(axis, target as HTMLElement, borderBoxSize);
          } catch (error) {
            caughtError ??= { error };
          }
        }
      }
    } finally {
      endAxisChangeBatch();
    }

    if (caughtError) {
      throw caughtError.error;
    }
  });

  resizeObserver.observe(axis.element);

  axis.items.forEach((item) => {
    assert(
      !itemIds.has(item.id),
      `Panel ids must be unique; id "${item.id}" was used more than once`
    );

    itemIds.add(item.id);

    if (item.onResize) {
      resizeObserver.observe(item.element);
    }
  });

  const axisSize = calculateAvailableAxisSize({ axis });

  // Calculate initial layout for the new Panel configuration
  const derivedItemConstraints = calculateItemConstraints(axis, axisSize);
  const defaultLayoutUnsafe = getDefaultLayout({
    axis,
    itemConstraints: derivedItemConstraints
  });
  const defaultLayoutSafe = validateAxisLayout({
    layout: defaultLayoutUnsafe,
    itemConstraints: derivedItemConstraints
  });

  const ownerDocument = axis.element.ownerDocument;
  const ownerWindow = ownerDocument.defaultView;

  ownerDocumentReferenceCounts.set(
    ownerDocument,
    (ownerDocumentReferenceCounts.get(ownerDocument) ?? 0) + 1
  );

  const separatorToItems: SeparatorToItemsMap = new Map();

  // Include disabled separators because enabling them later does not rebuild this map.
  // The keyboard handler checks disabled state before resizing.
  const hitRegions = calculateHitRegions({ axis, includeDisabled: true });
  hitRegions.forEach((hitRegion) => {
    if (hitRegion.separator) {
      separatorToItems.set(hitRegion.separator, hitRegion.items);
    }
  });

  updateMountedAxis(axis, {
    defaultLayoutDeferred: axisSize === 0,
    derivedItemConstraints,
    axisSize,
    layout: defaultLayoutSafe,
    requestedAxisSize: axisSize,
    requestedLayout: normalizeLayout({
      itemIds: axis.items.map(({ id }) => id),
      layout: defaultLayoutUnsafe
    }),
    separatorToItems
  });

  axis.separators.forEach((separator) => {
    assert(
      !separatorIds.has(separator.id),
      `Separator ids must be unique; id "${separator.id}" was used more than once`
    );

    separatorIds.add(separator.id);

    separator.element.addEventListener("keydown", onDocumentKeyDown);
  });

  // Cursor styles follow interaction state, for every document
  // Subscribe lazily (rather than at module scope) so bundlers can't drop the subscription as an unused side effect
  if (!didSubscribeToInteractionState) {
    didSubscribeToInteractionState = true;
    subscribeToInteractionState(updateCursorStyles);
  }

  // If this is the first group to be mounted, initialize event handlers
  if (ownerDocumentReferenceCounts.get(ownerDocument) === 1) {
    ownerDocument.addEventListener("contextmenu", onDocumentContextMenu, true);
    ownerDocument.addEventListener("dblclick", onDocumentDoubleClick, true);
    ownerDocument.addEventListener(
      "lostpointercapture",
      onDocumentLostPointerCapture,
      true
    );
    ownerDocument.addEventListener(
      "pointercancel",
      onDocumentPointerCancel,
      true
    );
    ownerDocument.addEventListener("pointerdown", onDocumentPointerDown, true);
    ownerDocument.addEventListener("pointerleave", onDocumentPointerLeave);
    ownerDocument.addEventListener("pointermove", onDocumentPointerMove);
    ownerDocument.addEventListener("pointerout", onDocumentPointerOut);
    ownerDocument.addEventListener("pointerup", onDocumentPointerUp, true);
    ownerWindow?.addEventListener("blur", onWindowBlur);
    ownerWindow?.addEventListener("pagehide", onWindowPageHide);
  }

  return function unmountAxis() {
    isMounted = false;

    ownerDocumentReferenceCounts.set(
      ownerDocument,
      Math.max(0, (ownerDocumentReferenceCounts.get(ownerDocument) ?? 0) - 1)
    );

    deleteMutableAxis(axis);
    removeAxisFromInteraction(axis);

    axis.separators.forEach((separator) => {
      separator.element.removeEventListener("keydown", onDocumentKeyDown);
    });

    // If this was the last group to be mounted, tear down event handlers
    if (!ownerDocumentReferenceCounts.get(ownerDocument)) {
      ownerDocument.removeEventListener(
        "contextmenu",
        onDocumentContextMenu,
        true
      );
      ownerDocument.removeEventListener(
        "dblclick",
        onDocumentDoubleClick,
        true
      );
      ownerDocument.removeEventListener(
        "lostpointercapture",
        onDocumentLostPointerCapture,
        true
      );
      ownerDocument.removeEventListener(
        "pointercancel",
        onDocumentPointerCancel,
        true
      );
      ownerDocument.removeEventListener(
        "pointerdown",
        onDocumentPointerDown,
        true
      );
      ownerDocument.removeEventListener("pointerleave", onDocumentPointerLeave);
      ownerDocument.removeEventListener("pointermove", onDocumentPointerMove);
      ownerDocument.removeEventListener("pointerout", onDocumentPointerOut);
      ownerDocument.removeEventListener("pointerup", onDocumentPointerUp, true);
      ownerWindow?.removeEventListener("blur", onWindowBlur);
      ownerWindow?.removeEventListener("pagehide", onWindowPageHide);
    }

    resizeObserver.disconnect();
  };
}
